import React, { useState, useEffect } from 'react'
import TrueFalseQuestion from '../quiz/TrueFalseQuestion'

const DSSchemaTest: React.FC = () => {
  const [dsQuestions, setDsQuestions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [showResults, setShowResults] = useState(false)

  const loadDSQuestions = async () => {
    setLoading(true)
    try {
      // Load DS database directly
      const response = await fetch('/bank/L11/C1/B01/DS/questions.db')
      if (!response.ok) {
        throw new Error(`Cannot load database: ${response.status}`)
      }

      // Load SQL.js
      const initSqlJs = (await import('sql.js')).default
      const SQL = await initSqlJs({
        locateFile: (file: string) => `https://sql.js.org/dist/${file}`
      })

      const arrayBuffer = await response.arrayBuffer()
      const db = new SQL.Database(new Uint8Array(arrayBuffer))

      // Get first 3 questions
      const result = db.exec('SELECT * FROM questions LIMIT 3')
      
      if (result.length > 0) {
        const columns = result[0].columns
        const rows = result[0].values

        const questions = rows.map(row => {
          const obj: any = { type: 'DS' }
          columns.forEach((col, index) => {
            obj[col] = row[index]
          })
          return obj
        })

        console.log('Loaded DS questions:', questions)
        setDsQuestions(questions)
      }

      db.close()
    } catch (error) {
      console.error('Error loading DS questions:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDSQuestions()
  }, [])

  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">DS Schema Test</h1>
      <p className="text-gray-600 mb-6">
        Test câu hỏi DS với schema thực tế: question_lead + statement_a/b/c/d + answer_a/b/c/d + solution_a/b/c/d
      </p>
      
      <div className="mb-6 flex gap-4">
        <button
          onClick={loadDSQuestions}
          disabled={loading}
          className="btn-primary px-4 py-2 rounded disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Reload DS Questions'}
        </button>
        
        <button
          onClick={() => setShowResults(!showResults)}
          className="btn-secondary px-4 py-2 rounded"
        >
          {showResults ? 'Hide Results' : 'Show Results'}
        </button>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="text-lg">Đang tải câu hỏi DS...</div>
        </div>
      )}

      <div className="space-y-8">
        {dsQuestions.map((question, index) => (
          <div key={question.id} className="border border-gray-300 rounded-lg p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Câu {index + 1}</h2>
              <div className="text-sm text-gray-600">
                ID: {question.id} | Type: {question.type}
              </div>
            </div>

            {/* Debug Schema Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
              <div className="font-medium mb-2">DS Schema Debug:</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div><strong>question_lead:</strong> {question.question_lead ? '✅' : '❌'}</div>
                <div><strong>statement_a:</strong> {question.statement_a ? '✅' : '❌'}</div>
                <div><strong>statement_b:</strong> {question.statement_b ? '✅' : '❌'}</div>
                <div><strong>statement_c:</strong> {question.statement_c ? '✅' : '❌'}</div>
                <div><strong>statement_d:</strong> {question.statement_d ? '✅' : '❌'}</div>
                <div><strong>answer_a:</strong> {question.answer_a !== null ? `${question.answer_a}` : '❌'}</div>
                <div><strong>answer_b:</strong> {question.answer_b !== null ? `${question.answer_b}` : '❌'}</div>
                <div><strong>answer_c:</strong> {question.answer_c !== null ? `${question.answer_c}` : '❌'}</div>
                <div><strong>answer_d:</strong> {question.answer_d !== null ? `${question.answer_d}` : '❌'}</div>
                <div><strong>solution_a:</strong> {question.solution_a ? '✅' : '❌'}</div>
                <div><strong>solution_b:</strong> {question.solution_b ? '✅' : '❌'}</div>
                <div><strong>solution_c:</strong> {question.solution_c ? '✅' : '❌'}</div>
                <div><strong>solution_d:</strong> {question.solution_d ? '✅' : '❌'}</div>
              </div>
            </div>

            {/* Raw Data Preview */}
            <details className="mb-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                View Raw Data
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs">
                <div className="space-y-1">
                  <div><strong>question_lead:</strong> {question.question_lead || '[NULL]'}</div>
                  <div><strong>statement_a:</strong> {question.statement_a || '[NULL]'}</div>
                  <div><strong>statement_b:</strong> {question.statement_b || '[NULL]'}</div>
                  <div><strong>statement_c:</strong> {question.statement_c || '[NULL]'}</div>
                  <div><strong>statement_d:</strong> {question.statement_d || '[NULL]'}</div>
                  <div><strong>answer_a:</strong> {question.answer_a !== null ? question.answer_a : '[NULL]'}</div>
                  <div><strong>answer_b:</strong> {question.answer_b !== null ? question.answer_b : '[NULL]'}</div>
                  <div><strong>answer_c:</strong> {question.answer_c !== null ? question.answer_c : '[NULL]'}</div>
                  <div><strong>answer_d:</strong> {question.answer_d !== null ? question.answer_d : '[NULL]'}</div>
                </div>
              </div>
            </details>

            {/* TrueFalseQuestion Component */}
            <TrueFalseQuestion
                      question={{
          ...question,
          // Bỏ hoàn toàn cau_hoi, chỉ sử dụng question_lead từ database
          question_lead: question.question_lead
        }}
              answers={answers}
              onAnswerChange={handleAnswerChange}
              showResults={showResults}
            />
          </div>
        ))}
      </div>

      {dsQuestions.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          Không có câu hỏi DS nào được tải
        </div>
      )}

      {/* Current Answers Debug */}
      {Object.keys(answers).length > 0 && (
        <div className="mt-8 p-4 bg-yellow-50 rounded-lg">
          <h3 className="font-medium mb-2">Current Answers:</h3>
          <pre className="text-sm text-gray-700 overflow-x-auto">
            {JSON.stringify(answers, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

export default DSSchemaTest